import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Save, ArrowLeft, User as UserIcon } from "lucide-react";
import { toast, Toaster } from "sonner";

const Field = ({ label, value, onChange, type = "text", required, testid, placeholder }) => (
  <label className="block">
    <span className="rs-label">
      {label} {required && <span className="text-[var(--rs-primary)]">*</span>}
    </span>
    <input
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      placeholder={placeholder}
      data-testid={testid}
      className="rs-input"
    />
  </label>
);

const Profile = () => {
  const { t } = useLanguage();
  const { setUser } = useAuth();
  const [form, setForm] = useState({
    name: "", phone: "", address: "", postal_code: "", city: "", license_number: "", date_of_birth: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/me/profile").then(({ data }) => {
      setForm({
        name: data.name || "",
        phone: data.phone || "",
        address: data.address || "",
        postal_code: data.postal_code || "",
        city: data.city || "",
        license_number: data.license_number || "",
        date_of_birth: data.date_of_birth || "",
      });
    }).catch(() => toast.error("Fehler beim Laden"));
  }, []);

  const upd = (k) => (val) => setForm({ ...form, [k]: val });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/me/profile", form);
      setUser(data);
      toast.success(t("profile_saved"));
    } catch (e) {
      toast.error("Fehler beim Speichern");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rs-app min-h-screen bg-white">
      <Header />
      <Toaster position="top-center" />
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-10">
        <Link to="/my" className="text-xs uppercase tracking-[0.2em] text-[var(--rs-text-muted)] hover:text-[var(--rs-text)] inline-flex items-center gap-2" data-testid="profile-back">
          <ArrowLeft size={12} /> {t("nav_my")}
        </Link>
        <div className="rs-overline mt-3">NIVARA · KUNDE</div>
        <h1 className="font-[Chivo] text-4xl sm:text-5xl font-black tracking-tight leading-[1] mt-2 flex items-center gap-3">
          <UserIcon size={32} /> {t("profile_title")}
        </h1>
        <p className="text-[var(--rs-text-muted)] mt-2">{t("profile_sub")}</p>

        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10" data-testid="profile-form">
          <Field label={t("f_full_name")} value={form.name} onChange={upd("name")} required testid="profile-name" />
          <Field label={t("f_phone")} value={form.phone} onChange={upd("phone")} type="tel" testid="profile-phone" />
          <Field label={t("f_address")} value={form.address} onChange={upd("address")} testid="profile-address" />
          <div className="grid grid-cols-3 gap-3">
            <Field label={t("f_postal")} value={form.postal_code} onChange={upd("postal_code")} testid="profile-postal" />
            <div className="col-span-2">
              <Field label={t("f_city")} value={form.city} onChange={upd("city")} testid="profile-city" />
            </div>
          </div>
          <Field label={t("f_license")} value={form.license_number} onChange={upd("license_number")} testid="profile-license" />
          <Field label={t("f_dob")} value={form.date_of_birth} onChange={upd("date_of_birth")} type="date" testid="profile-dob" />

          <div className="md:col-span-2 mt-4 flex justify-end">
            <button type="submit" className="rs-btn-primary" disabled={busy} data-testid="profile-save-btn">
              <Save size={16} className="mr-2" />
              {busy ? "…" : t("profile_save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Profile;
